import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { elapsedSeconds, formatDuration } from "@/features/driver/lib/driverPresentation.js";
import { useDriverLanguage } from "@/features/driver/i18n/useDriverLanguage";

interface WaitingTimerProps {
  startedAt?: string | null;
  baseSeconds?: number;
  freeMinutes?: number;
  maxMinutes?: number;
}

/**
 * Live count-up timer for waiting time. Shows previously-banked seconds
 * (baseSeconds) plus the currently-running segment (from startedAt). When
 * paused (no startedAt) it shows the frozen accumulated total.
 */
export default function WaitingTimer({ startedAt, baseSeconds = 0, freeMinutes, maxMinutes }: WaitingTimerProps) {
  const { t } = useDriverLanguage();
  const [seconds, setSeconds] = useState(() => baseSeconds + elapsedSeconds(startedAt));

  useEffect(() => {
    if (!startedAt) {
      setSeconds(baseSeconds);
      return;
    }

    setSeconds(baseSeconds + elapsedSeconds(startedAt));
    const interval = setInterval(() => setSeconds(baseSeconds + elapsedSeconds(startedAt)), 1000);

    return () => clearInterval(interval);
  }, [startedAt, baseSeconds]);

  if (!startedAt && baseSeconds <= 0) {
    return null;
  }

  const minutes = seconds / 60;
  const overFree = freeMinutes !== undefined && minutes > freeMinutes;
  const overMax = maxMinutes !== undefined && minutes >= maxMinutes;
  const tone = overMax ? "text-red-600" : overFree ? "text-amber-600" : "text-green-700";

  return (
    <div className="flex items-center gap-2 rounded-md bg-neutral-50 p-3" role="timer" aria-live="polite">
      <Clock className={`h-5 w-5 ${tone}`} aria-hidden="true" />
      <div>
        <span className="block text-xs text-neutral-500">{t("waitingTimeLabel")}</span>
        <strong className={`text-lg tabular-nums ${tone}`}>{formatDuration(seconds)}</strong>
      </div>
      {overMax && <span className="ml-auto text-xs font-medium text-red-600">{t("maxWaitReached")}</span>}
    </div>
  );
}
